import { useLayoutEffect, useRef, useState, type RefObject } from "react";

// Whether the element's content is cut off by a line clamp — true only when
// the text overflows its box, so a tooltip with the full text is offered only
// when something is actually hidden. Re-measured when `content` changes and
// when the element is resized (e.g. the tile narrowing with the window).
const useIsClamped = <T extends HTMLElement>(
  content: string | null
): { isClamped: boolean; ref: RefObject<T | null> } => {
  const ref = useRef<T>(null);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const measure = () =>
      setIsClamped(element.scrollHeight > element.clientHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [content]);

  return { isClamped, ref };
};

export default useIsClamped;
